import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faCircleExclamation, faCircleCheck, faScrewdriverWrench, faGear, faMicrochip } from '@fortawesome/free-solid-svg-icons';
import { TiltGauge } from '../../../components/graphs/TiltGauge';
import StatusBar from '../../../components/graphs/StatusBar';
import HistoricalTilt from '../../../components/graphs/HistoricalTilt';
import { apiFetch } from '../../../api/apiClient';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import { usePortfolioDateFormatter } from '../../../utils/usePortfolioDateFormatter';

function SolarTrackers() {
  const { plant: plantData } = usePlantData();
  const dateFormatter = usePortfolioDateFormatter();
  const [trackers, setTrackers] = useState([]);
  const [trackerOnline, setTrackerOnline] = useState({ online: 0, total: 0 });
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });

  useEffect(() => {
    document.title = "Vivosol Plant Performance Insights Hub | Gemini / Diagnostics / SolarTrackers";
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTrackers = async () => {
      try {
        const data = await apiFetch('/api/metrics/portfolio/gemini/solar-trackers');
        if (isMounted) {
          const list = Array.isArray(data) ? data : (data?.trackers ?? []);
          setTrackers(list);
          setTrackerOnline({
            online: data?.online_trackers ?? list.filter(
              (t) => String(t?.statusTracker ?? '').toLowerCase() === 'green'
            ).length,
            total: data?.total_trackers ?? list.length,
          });
        }
      } catch (error) {
        console.error('Failed to load solar trackers:', error);
        if (isMounted) {
          setTrackers([]);
          setTrackerOnline({ online: 0, total: 0 });
        }
      }
    };

    loadTrackers();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <Marquee />
        <ChartTooltip tooltip={tooltip} transition={false} />
        <h1 className="mt-3 mb-0"><Link to={`${plantData.dashboard}`}><strong>{plantData.plant_name}</strong></Link> : {plantData.config} ({plantData.location})</h1>
        <Breadcrumbs labels={{ portfolioName: plantData.plant_name }} />
        <div className="trackers mb-5">
          {trackers.map((tracker, index) => {
            const tilt = Number(tracker?.currentTilt ?? 0);
            const trackerNumber = String(tracker?.trackerId ?? (index + 1)).replace(/\D+/g, '') || String(index + 1);
            const tiltClass = tilt >= 0 ? 'green' : 'yellow';
            return (
              <div key={tracker?.trackerId ?? index} className={`card tm${trackerNumber} ${tiltClass}`}>
                <span className="title">
                  <span>{`TRACKER MOTOR ${trackerNumber}`}</span>
                  <FontAwesomeIcon icon={tilt >= 0 ? faCircleCheck : faCircleExclamation} />
                </span>
                <div className="metric">
                  {tilt}
                </div>
              </div>
            );
          })}
        </div>
        <div className="tiltGauges mb-5">
          {trackers.map((tracker, index) => {
            const tilt = Number(tracker?.currentTilt ?? 0);
            const trackerNumber = String(tracker?.trackerId ?? (index + 1)).replace(/\D+/g, '') || String(index + 1);
            return (
              <div key={`gauge-${tracker?.trackerId ?? index}`} className={`tm${trackerNumber}`}>
                <TiltGauge tilt={tilt} label={`Tracker Motor ${trackerNumber}`} />
              </div>
            );
          })}
        </div>
        <div id="trackerAnomalies" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faMicrochip} /> SOLAR TRACKER ANOMALIES</span> - <span className="online">(<strong>TRACKERS ONLINE</strong>: {trackerOnline.online} / {trackerOnline.total})</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button><FontAwesomeIcon icon={faGear} /></button>
            </div>
          </div>
          <div>
            <table className="table">
              <thead>
              <tr>
                <td>Status</td>
                <td>Tracker</td>
                <td>Degree Tilt</td>
                <td>Device</td>
                <td>Timestamp</td>
                <td>FBAVG</td>
                <td>SPAVG</td>
                <td></td>
              </tr>
              </thead>
              <tbody>
              {trackers.map((tracker, index) => {
                const trackerNumber = String(tracker?.trackerId ?? (index + 1)).replace(/\D+/g, '') || String(index + 1);
                const status = String(tracker?.statusTracker ?? '').toLowerCase();
                const statusClass = ['red', 'yellow', 'green'].includes(status) ? status : null;
                const statusIcon = statusClass === 'green'
                  ? faCircleCheck
                  : statusClass === 'red'
                    ? faCircleXmark
                    : faCircleExclamation;
                const alarmDegreeTilt = Number(tracker?.alarmDegreeTilt ?? 0);

                return (
                  <tr key={`anomaly-${tracker?.trackerId ?? index}`}>
                    <td>
                      {statusClass ? (
                        <FontAwesomeIcon className={statusClass} icon={statusIcon} />
                      ) : (
                        <span title="Status unsigned">—</span>
                      )}
                    </td>
                    <td>{`TM ${trackerNumber}`}</td>
                    <td><StatusBar value={alarmDegreeTilt} type="angle" isNegative={alarmDegreeTilt < 0} /></td>
                    <td>{tracker?.device ?? '-'}</td>
                    <td>{tracker?.timestamp ? dateFormatter.formatUtc(tracker.timestamp) : '-'}</td>
                    <td>{tracker?.fbavg ?? '-'}</td>
                    <td>{tracker?.spavg ?? '-'}</td>
                    <td><button><FontAwesomeIcon icon={faScrewdriverWrench} /></button></td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
        <div id="historicalTilt" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faMicrochip} /> HISTORICAL TILT STATUS</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button><FontAwesomeIcon icon={faGear} /></button>
            </div>
          </div>
          <div>
            <HistoricalTilt
              portfolioName={plantData.portfolio_name}
              onTooltip={setTooltip}
              timeZone={dateFormatter.timeZone}
            />
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default SolarTrackers;
